import React, {useCallback, useMemo, useRef, useState} from 'react';
import cn from 'classnames';

import {CarouselProvider} from './CarouselContext';
import {ICarouselProps} from './ICarousel';
import {CarouselSwiper} from './Subcomponents';

import styles from './Carousel.module.scss';

const Carousel = ({children, config, handlers, classNames}: ICarouselProps) => {
	const carouselRef = useRef(null);

	const [registeredClassNames, setRegisteredClassNames] = useState<ICarouselProps['classNames']>({});

	const registerCarouselClassName = useCallback((key: keyof ICarouselProps['classNames'], className: string) => {
		setRegisteredClassNames((prevRegisteredClassNames) => {
			const keyClassNames = prevRegisteredClassNames[key] || '';

			// Go out if the className is already registered with the given key
			if (keyClassNames.includes(className)) {
				return prevRegisteredClassNames;
			}

			// Add the className to the keyClassNames string
			const newKeyClassNames = `${keyClassNames} ${className}`.trim();

			// Update the state
			return {
				...prevRegisteredClassNames,
				[key]: newKeyClassNames,
			};
		});
	}, []);

	const unRegisterCarouselClassName = useCallback((key: keyof ICarouselProps['classNames'], className: string) => {
		setRegisteredClassNames((prevRegisteredClassNames) => {
			const keyClassNames = prevRegisteredClassNames[key] || '';

			// Go out if the className is not registered with the given key
			if (!keyClassNames.includes(className)) {
				return prevRegisteredClassNames;
			}

			// Remove the className from the keyClassNames string
			const newKeyClassNames = keyClassNames.replace(className, '').trim();

			// Update the state
			return {
				...prevRegisteredClassNames,
				[key]: newKeyClassNames,
			};
		});
	}, []);

	const memoizedProviderValue = useMemo(
		() => ({
			isInCarousel: true,
			registerCarouselClassName,
			unRegisterCarouselClassName
		}),
		[registerCarouselClassName],
	);

	return (
		<CarouselProvider value={memoizedProviderValue}>
			<CarouselSwiper
				ref={carouselRef}
				classNames={{
					pagination: cn(styles['pagination'], registeredClassNames?.pagination),
					bullet: cn('bullet', classNames?.bullet, registeredClassNames?.bullet),
					bulletActive: cn('bullet-active', classNames?.bulletActive, registeredClassNames?.bulletActive),
					slide: cn(styles['slide'], classNames?.slide, registeredClassNames?.slide),
					swiper: cn(classNames?.swiper, registeredClassNames?.swiper),
					navigation: cn(classNames?.navigation, registeredClassNames?.navigation),
				}}
				handlers={handlers}
				config={config}
			>
				{children}
			</CarouselSwiper>
		</CarouselProvider>
	);
};

export default Carousel;
